import React, { useState, useRef, useEffect, Fragment, useMemo } from 'react';

import classnames from 'classnames';

/**
 * Details about Intersection Observer API
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

const useIntersect = ({ root = null, rootMargin, threshold = 0 }) => {
  const [entry, updateEntry] = useState({});
  const [node, setNode] = useState(null);

  const observer = useRef(null);

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      ([newEntry]) => {
        if (newEntry.target !== entry.target) {
          updateEntry(newEntry);
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    const { current: currentObserver } = observer;

    if (node) {
      currentObserver.observe(node);
    }
    return () => currentObserver.disconnect();
  }, [node, root, rootMargin, threshold]);

  return [setNode, entry.isIntersecting];
};

const IntersectionDetector = ({
  animationClasses,
  triggerOnce = true,
  children,
}) => {
  const [ref, isIntersecting] = useIntersect({
    threshold: 0,
  });

  const [animationHasBeenApplied, setAnimationHasBeenApplied] = useState(false);
  const [activeAnimationClasses, setActiveAnimationClasses] =
    useState(undefined);

  useEffect(() => {
    if (isIntersecting) {
      if (triggerOnce) {
        if (!animationHasBeenApplied) {
          setAnimationHasBeenApplied(!animationHasBeenApplied);
          setActiveAnimationClasses(animationClasses);
        }
      } else {
        setActiveAnimationClasses(animationClasses);
      }
    } else {
      setActiveAnimationClasses(undefined);
    }
  }, [triggerOnce, isIntersecting]);

  return (
    <Fragment>
      {/* we're only ever cloning a single child, for the cases in which we 
      want to apply animation classes to, which is to say that at this moment, 
      we're not considering applying animations to elements in bulk */}
      {React.cloneElement(children, {
        className: classnames(children.props.className, activeAnimationClasses),
      })}
      {/* This div is the one being used to determine whether or not we are 
      crossing the browser intersection and in turn applies the animation classes */}
      <div
        ref={ref}
        style={{
          position: 'relative',
          opacity: 0,
          top: '-100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      />
    </Fragment>
  );
};

const useOnScreen = (ref) => {
  const [isIntersecting, setIntersecting] = useState(true);

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting)
      ),
    [ref]
  );

  useEffect(() => {
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return isIntersecting;
};

export { IntersectionDetector, useOnScreen };
